import { Head, Link } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowRight, Bot, FileText, KeyRound, Layers, ListChecks, Repeat } from "lucide-react"
import { BetaBadge } from "@/components/BetaBadge"
import { Button } from "@/components/ui/button"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"

const FEATURES = [
  {
    icon: Layers,
    title: { id: "home.f_flashcards_title" },
    body: { id: "home.f_flashcards_body" },
  },
  {
    icon: Repeat,
    title: { id: "home.f_review_title" },
    body: { id: "home.f_review_body" },
  },
  {
    icon: ListChecks,
    title: { id: "home.f_quizzes_title" },
    body: { id: "home.f_quizzes_body" },
  },
  {
    icon: FileText,
    title: { id: "home.f_sources_title" },
    body: { id: "home.f_sources_body" },
  },
  {
    icon: Bot,
    title: { id: "home.f_assistant_title" },
    body: { id: "home.f_assistant_body" },
  },
]

type ProgramLink = { name: string; slug: string; tagline: string | null }

export default function Home({ programs }: { programs: ProgramLink[] }) {
  const intl = useIntl()
  return (
    <>
      <Head title={intl.formatMessage({ id: "home.title" })}>
        <meta name="description" content={intl.formatMessage({ id: "home.meta" })} />
        <meta property="og:title" content={intl.formatMessage({ id: "home.og_title" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "home.meta" })} />
        <meta property="og:type" content="website" />
      </Head>

      <div className="min-h-screen bg-page text-ink-body">
        <header className="border-b border-hairline">
          <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
            <div className="flex items-center gap-2">
              <Link href="/" className="flex items-center gap-2 text-ink-display no-underline">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-faded font-display text-sm font-semibold text-accent">
                  Q
                </span>
                <span className="font-display text-sm font-semibold">Quiziqal</span>
              </Link>
              <BetaBadge />
            </div>
            <nav className="flex items-center gap-2">
              <Button asChild variant="ghost" size="sm">
                <Link href="/login">{intl.formatMessage({ id: "home.log_in" })}</Link>
              </Button>
              <Button asChild size="sm">
                <Link href="/signup">{intl.formatMessage({ id: "home.sign_up" })}</Link>
              </Button>
            </nav>
          </div>
        </header>

        <main>
          <section className="mx-auto max-w-3xl px-4 pb-16 pt-16 text-center sm:px-6 sm:pt-24">
            <p className="text-sm text-accent">{intl.formatMessage({ id: "home.eyebrow" })}</p>
            <h1 className="mt-3 text-4xl sm:text-5xl">{intl.formatMessage({ id: "home.headline" })}</h1>
            <p className="mx-auto mt-4 max-w-2xl">
              {intl.formatMessage({ id: "home.intro" })}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg">
                <Link href="/signup">{intl.formatMessage({ id: "home.start_free" })}</Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link href="/login">{intl.formatMessage({ id: "home.log_in" })}</Link>
              </Button>
            </div>
          </section>

          <section className="border-t border-hairline bg-surface">
            <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
              <h2 className="text-center text-2xl">{intl.formatMessage({ id: "home.features_heading" })}</h2>
              <ul className="mt-10 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
                {FEATURES.map(({ icon: Icon, title, body }) => (
                  <li key={title.id} className="rounded-xl border border-hairline bg-page p-5 shadow-xs">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-faded text-accent">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="mt-4">{intl.formatMessage(title)}</h3>
                    <p className="mt-1">{intl.formatMessage(body)}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
            <div className="callout callout-accent flex flex-col gap-4 sm:flex-row sm:items-start">
              <KeyRound className="h-6 w-6 shrink-0 text-accent" />
              <div>
                <h2 className="text-2xl">{intl.formatMessage({ id: "home.exam_heading" })}</h2>
                <p className="mt-2">
                  {intl.formatMessage({ id: "home.exam_body" })}
                </p>
                {programs.length > 0 && (
                  <ul className="mt-4 list-none space-y-2 p-0">
                    {programs.map((program) => (
                      <li key={program.slug}>
                        <Link href={`/p/${program.slug}`} className="inline-flex items-center gap-1">
                          {program.name}
                          {program.tagline ? `: ${program.tagline}` : ""} <ArrowRight className="h-4 w-4" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <section className="border-t border-hairline">
            <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
              <h2 className="text-2xl">{intl.formatMessage({ id: "home.final_heading" })}</h2>
              <p className="mx-auto mt-2 max-w-xl">{intl.formatMessage({ id: "home.final_body" })}</p>
              <div className="mt-6">
                <Button asChild size="lg">
                  <Link href="/signup">{intl.formatMessage({ id: "home.create_account" })}</Link>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <footer className="border-t border-hairline">
          <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-8 text-xs text-ink-muted sm:flex-row sm:justify-between sm:px-6">
            <span>Quiziqal</span>
            <LanguageSwitcher id="footer-language-switcher" className="w-auto text-xs sm:order-last" />
          </div>
        </footer>
      </div>
    </>
  )
}
